'use client';

import { createContext, type ReactElement, type ReactNode, useContext, useEffect, useState } from 'react';

import { localStorageManager } from '@/infrastructure/persistence/managers/LocalStorageManager';

export type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

type ThemeContextType = {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  preference: 'system',
  resolvedTheme: 'light',
  setPreference: () => {},
});

const CANVAS: Record<ResolvedTheme, string> = { light: '#f6f6f6', dark: '#0a0a0a' };

const resolve = (preference: ThemePreference): ResolvedTheme => {
  if (preference === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return preference;
};

const applyTheme = (resolved: ResolvedTheme): void => {
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', CANVAS[resolved]);
};

/**
 * Owns the theme PREFERENCE (light/dark/system, persisted) and keeps `<html data-theme>`
 * truthful; the library components only observe the attribute. The inline script in
 * layout.tsx paints the first frame before this mounts.
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactElement {
  const [preference, setPreferenceState] = useState<ThemePreference>('system');
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');

  useEffect(() => {
    const stored = localStorageManager.getItem<ThemePreference>('theme');
    const initial = stored === 'dark' || stored === 'light' || stored === 'system' ? stored : 'system';
    const resolved = resolve(initial);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreferenceState(initial);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, []);

  useEffect(() => {
    if (preference !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => {
      const resolved = resolve('system');
      setResolvedTheme(resolved);
      applyTheme(resolved);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = (next: ThemePreference): void => {
    setPreferenceState(next);
    localStorageManager.setItem('theme', next);
    const resolved = resolve(next);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  };

  return <ThemeContext.Provider value={{ preference, resolvedTheme, setPreference }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextType {
  return useContext(ThemeContext);
}
